import React from 'react';
import { motion } from 'framer-motion';
import { Quote, Flame, Waves, Sparkles, Sunrise, Activity } from 'lucide-react';
import { usePageContent } from '../hooks/usePageContent';
import { usePageTitle } from '../hooks/usePageTitle';

const TEACHING_ICONS = [Flame, Activity, Waves, Sparkles, Sunrise];

// Section 5 — hardcoded canonical Mahavakyas (do not edit via Admin)
const CLASSICAL_MAHAVAKYAS = [
  {
    sanskrit: 'प्रज्ञानं ब्रह्म',
    transliteration: 'Prajnanam Brahma',
    meaning: 'Consciousness is Brahman',
    origin: 'Aitareya Upanishad',
    veda: 'Rigveda',
  },
  {
    sanskrit: 'अयम् आत्मा ब्रह्म',
    transliteration: 'Ayam Atma Brahma',
    meaning: 'This Self is Brahman',
    origin: 'Mandukya Upanishad',
    veda: 'Atharvaveda',
  },
  {
    sanskrit: 'तत् त्वम् असि',
    transliteration: 'Tat Tvam Asi',
    meaning: 'That Thou Art',
    origin: 'Chandogya Upanishad',
    veda: 'Samaveda',
  },
  {
    sanskrit: 'अहं ब्रह्मास्मि',
    transliteration: 'Aham Brahmasmi',
    meaning: 'I am Brahman',
    origin: 'Brihadaranyaka Upanishad',
    veda: 'Yajurveda',
  },
];

export const Mentor = () => {
  usePageTitle('Our Mentor');
  const { t } = usePageContent('mentor');

  const teachings = [
    {
      title: t('teaching_1_title', 'The Thritvam Path (The Three Pillars)'),
      description: t(
        'teaching_1_description',
        'Mantra Japa (Sound Vibration), Dhyana (Meditation), and Agni Kriya (Fire Action) — a foundational methodology leading seekers toward inner peace, mental clarity, and deep spiritual purification.'
      ),
    },
    {
      title: t('teaching_2_title', 'Mapping the Human Energy System'),
      description: t(
        'teaching_2_description',
        'A subtle scientific analysis of the human energy body comprising 54 components and 5 fundamental energies, designed to diagnose and resolve the root energetic causes of human suffering.'
      ),
    },
    {
      title: t('teaching_3_title', 'Bhaishajya Mahayagas & Environmental Renewal'),
      description: t(
        'teaching_3_description',
        'Transforming ritual into science through medicinal fire rituals (Bhaishajya Mahayagas) and Agni Kriyas that purify the subtle realms, restore environmental equilibrium, and alleviate physical-mental disorders.'
      ),
    },
    {
      title: t('teaching_4_title', 'Sankalpa Shakti & Modern Harmony'),
      description: t(
        'teaching_4_description',
        'Harnessing the power of resolve (Sankalpa) to direct cosmic energy, bridging Vedic philosophy with quantum physics and neuroplasticity for complete material-spiritual balance.'
      ),
    },
    {
      title: t('teaching_5_title', 'Dawn of the Sathya Yuga'),
      description: t(
        'teaching_5_description',
        'Inspiring humanity to elevate its frequency and become active co-travelers in the transition toward the Age of Truth (Sathya Yuga).'
      ),
    },
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="mentor-page">
      {/* ---------- Section 1: Hero Banner ---------- */}
      <section className="py-24 md:py-32 bg-cream" data-testid="mentor-hero-section">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="text-saffron-600 uppercase text-xs md:text-sm tracking-[0.25em] font-body font-semibold mb-5">
              {t('page_overline', 'GUIDING LIGHT OF RISHIDEVISM')}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-charcoal-800 tracking-tighter leading-none mb-6">
              {t('page_title', 'Risheeshwar Narendranji')}
            </h1>
            <p className="text-lg md:text-xl text-charcoal-600 font-body leading-relaxed max-w-3xl mx-auto">
              {t(
                'page_subtitle',
                'Bridging Ancient Vedic Science with Modern Consciousness for the Elevation of Humanity.'
              )}
            </p>
          </motion.div>
        </div>
      </section>

      {/* ---------- Section 2: Mentor Profile ---------- */}
      <section className="py-20 md:py-28 bg-white" data-testid="mentor-profile-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <img
                src={t('mentor_image', 'https://images.unsplash.com/photo-1746221419914-527c5d88d7f0?q=85')}
                alt={t('mentor_name', 'Risheeshwar Narendranji')}
                className="rounded-xl shadow-[0_8px_30px_rgba(44,30,22,0.12)] w-full object-cover"
                data-testid="mentor-portrait"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="text-saffron-600 uppercase text-xs tracking-[0.2em] font-body font-semibold mb-3">
                {t('profile_overline', 'About Guruji')}
              </div>
              <h2 className="text-3xl md:text-4xl font-heading tracking-tight leading-snug text-charcoal-800 mb-3">
                {t('mentor_name', 'Risheeshwar Narendranji')}
              </h2>
              <p className="text-base md:text-lg text-saffron-700 font-body font-medium mb-6">
                {t('profile_headline', 'Distinguished Spiritual Reformer & Saint')}
              </p>
              <p className="text-base md:text-lg leading-relaxed text-charcoal-600 mb-5">
                {t(
                  'mentor_bio_1',
                  'Guruji Risheeshwar Narendranji is an engineer-turned-spiritual guide whose life work illuminates the scientific, practical, and inner dimensions of Sanatan Dharma.'
                )}
              </p>
              <p className="text-base md:text-lg leading-relaxed text-charcoal-600 mb-5">
                {t(
                  'mentor_bio_2',
                  'His entire life and philosophy are dedicated to the elevation of human consciousness, environmental purification, and the harmonizing of ancient Vedic wisdom with modern lifestyles.'
                )}
              </p>
              <p className="text-base md:text-lg leading-relaxed text-charcoal-600">
                {t('mentor_bio_3', '')}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ---------- Section 2b: Tools We Teach the Seekers ---------- */}
      <section className="py-16 md:py-24 bg-white border-t border-charcoal-50" data-testid="mentor-tools-section">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="text-saffron-600 uppercase text-xs tracking-[0.2em] font-body font-semibold mb-3">
              {t('tools_overline', 'Sacred Sadhana')}
            </div>
            <h2 className="text-3xl md:text-5xl font-heading tracking-tight text-charcoal-800 mb-4">
              {t('tools_heading', 'Tools We Teach the Seekers')}
            </h2>
            <p className="text-base md:text-lg text-charcoal-600 max-w-3xl mx-auto leading-relaxed">
              {t(
                'tools_description',
                'Three timeless practices — the Thritvam path — that Guruji offers as living instruments of transformation.',
              )}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {[
              {
                idx: 1,
                icon: Waves,
                titleKey: 'tool_1_title',
                titleDefault: 'Mantra Japa',
                descKey: 'tool_1_description',
                descDefault:
                  'The rhythmic repetition of sacred sound — attuning consciousness to the primordial vibration that gave rise to all creation.',
              },
              {
                idx: 2,
                icon: Sparkles,
                titleKey: 'tool_2_title',
                titleDefault: 'Meditation',
                descKey: 'tool_2_description',
                descDefault:
                  'Dhyana — the still turning-inward of awareness, revealing the witnessing Self beyond thought, name, and form.',
              },
              {
                idx: 3,
                icon: Flame,
                titleKey: 'tool_3_title',
                titleDefault: 'Fire Rituals',
                descKey: 'tool_3_description',
                descDefault:
                  'Agni Kriya and Bhaishajya Mahayagas — sacred flame practices that purify subtle realms and restore harmony to body, mind, and environment.',
              },
            ].map((tool) => {
              const Icon = tool.icon;
              return (
                <motion.div
                  key={tool.idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: tool.idx * 0.08 }}
                  viewport={{ once: true }}
                  className="bg-gradient-to-br from-cream via-white to-saffron-50 rounded-2xl p-8 border-2 border-saffron-100 hover:border-saffron-300 transition-colors"
                  data-testid={`mentor-tool-card-${tool.idx}`}
                >
                  <div className="w-14 h-14 rounded-full bg-saffron-100 flex items-center justify-center mb-5 text-saffron-700">
                    <Icon size={26} />
                  </div>
                  <h3 className="text-xl md:text-2xl font-heading text-charcoal-800 mb-3 leading-snug">
                    {t(tool.titleKey, tool.titleDefault)}
                  </h3>
                  <p className="text-sm md:text-base text-charcoal-600 leading-relaxed">
                    {t(tool.descKey, tool.descDefault)}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ---------- Section 3: Featured Quote ---------- */}
      <section className="py-16 md:py-24 bg-cream" data-testid="mentor-quote-section">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-saffron-50 to-gold-100 rounded-2xl p-12 md:p-16 text-center relative overflow-hidden"
          >
            <Quote className="absolute top-6 left-6 text-saffron-300" size={44} />
            <Quote className="absolute bottom-6 right-6 text-saffron-300 rotate-180" size={44} />
            <blockquote className="text-2xl md:text-3xl font-heading text-charcoal-800 leading-relaxed italic mb-6 relative z-10">
              &ldquo;{t('quote_text', 'Consciousness governs energy, and energy rules the world.')}&rdquo;
            </blockquote>
            <p className="text-base md:text-lg font-body font-semibold text-saffron-700">
              — {t('quote_source', 'Risheeshwar Narendranji')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* ---------- Section 4: Key Teachings ---------- */}
      <section className="py-20 md:py-28 bg-white" data-testid="mentor-teachings-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <div className="text-saffron-600 uppercase text-xs tracking-[0.2em] font-body font-semibold mb-3">
              {t('teachings_overline', 'Pillar Philosophies')}
            </div>
            <h2 className="text-3xl md:text-5xl font-heading tracking-tight text-charcoal-800">
              {t('teachings_heading', 'Key Teachings & Pillar Philosophies')}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {teachings.map((item, index) => {
              const Icon = TEACHING_ICONS[index % TEACHING_ICONS.length];
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-xl shadow-[0_8px_30px_rgba(44,30,22,0.06)] border border-charcoal-100 p-7 hover:shadow-[0_12px_40px_rgba(44,30,22,0.1)] transition-shadow"
                  data-testid={`teaching-card-${index}`}
                >
                  <div className="w-12 h-12 rounded-full bg-saffron-50 flex items-center justify-center mb-4">
                    <Icon className="text-saffron-600" size={22} />
                  </div>
                  <h3 className="text-xl font-heading text-charcoal-800 mb-3 leading-snug">
                    {item.title}
                  </h3>
                  <p className="text-sm md:text-base text-charcoal-600 leading-relaxed">
                    {item.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ---------- Section 5: Four Classical Mahavakyas (HARDCODED) ---------- */}
      <section className="py-20 md:py-28 bg-cream" data-testid="mentor-mahavakyas-section">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="text-saffron-600 uppercase text-xs tracking-[0.2em] font-body font-semibold mb-3">
              The Foundational Truths
            </div>
            <h2 className="text-3xl md:text-5xl font-heading tracking-tight text-charcoal-800 mb-5">
              The Four Classical Mahavakyas
            </h2>
            <p className="text-base md:text-lg text-charcoal-600 max-w-3xl mx-auto leading-relaxed">
              In Vedic philosophy, the four principal Upanishads reveal the core non-dual truths (Advaita) of existence.
            </p>
          </motion.div>

          {/* Table (desktop) */}
          <div className="hidden md:block bg-white rounded-2xl shadow-[0_8px_30px_rgba(44,30,22,0.08)] border border-charcoal-100 overflow-hidden">
            <table className="w-full">
              <thead className="bg-saffron-50 border-b border-saffron-100">
                <tr>
                  <th className="text-left px-6 py-4 font-heading text-charcoal-800 text-sm uppercase tracking-wider">Sacred Utterance</th>
                  <th className="text-left px-6 py-4 font-heading text-charcoal-800 text-sm uppercase tracking-wider">Meaning</th>
                  <th className="text-left px-6 py-4 font-heading text-charcoal-800 text-sm uppercase tracking-wider">Origin</th>
                  <th className="text-left px-6 py-4 font-heading text-charcoal-800 text-sm uppercase tracking-wider">Veda</th>
                </tr>
              </thead>
              <tbody>
                {CLASSICAL_MAHAVAKYAS.map((m, i) => (
                  <tr key={i} className="border-b border-charcoal-100 last:border-0 hover:bg-cream/40 transition-colors">
                    <td className="px-6 py-5">
                      <div className="text-lg font-heading text-saffron-700 mb-1">{m.sanskrit}</div>
                      <div className="text-sm italic text-charcoal-500">{m.transliteration}</div>
                    </td>
                    <td className="px-6 py-5 text-charcoal-700">{m.meaning}</td>
                    <td className="px-6 py-5 text-charcoal-600 text-sm">{m.origin}</td>
                    <td className="px-6 py-5 text-charcoal-600 text-sm">{m.veda}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Cards (mobile) */}
          <div className="md:hidden space-y-4">
            {CLASSICAL_MAHAVAKYAS.map((m, i) => (
              <div key={i} className="bg-white rounded-xl border border-charcoal-100 p-5 shadow-sm">
                <div className="text-2xl font-heading text-saffron-700 mb-1">{m.sanskrit}</div>
                <div className="text-sm italic text-charcoal-500 mb-3">{m.transliteration}</div>
                <div className="text-charcoal-700 mb-2 text-sm">{m.meaning}</div>
                <div className="flex justify-between text-xs text-charcoal-500 pt-3 border-t border-charcoal-100">
                  <span>{m.origin}</span>
                  <span className="font-semibold">{m.veda}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- Section 6: Fifth Mahavakya — Urjjameva Brahma ---------- */}
      <section className="py-20 md:py-28 bg-white" data-testid="mentor-fifth-mahavakya-section">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="relative bg-gradient-to-br from-cream via-white to-saffron-50 rounded-2xl border-2 border-saffron-200 shadow-[0_10px_50px_rgba(217,119,6,0.12)] p-8 md:p-14"
          >
            <div className="text-center mb-10">
              <div className="text-saffron-600 uppercase text-xs tracking-[0.25em] font-body font-semibold mb-4">
                {t('fifth_overline', 'The Fifth Mahavakya')}
              </div>
              <div className="text-5xl md:text-6xl font-heading text-saffron-700 mb-4 leading-none">
                {t('fifth_sanskrit', 'ऊर्जैव ब्रह्म')}
              </div>
              <div className="text-2xl md:text-3xl italic text-charcoal-700 font-heading mb-3">
                {t('fifth_transliteration', 'Urjjameva Brahma')}
              </div>
              <h2 className="text-xl md:text-2xl font-heading text-charcoal-800 leading-snug max-w-2xl mx-auto">
                {t('fifth_heading', 'The Contemporary Expansion by Risheeshwar Narendranji')}
              </h2>
            </div>

            <p className="text-base md:text-lg text-charcoal-700 leading-relaxed mb-10 text-center max-w-3xl mx-auto">
              {t(
                'fifth_intro',
                'To bridge ancient Vedantic metaphysics with modern physics, Risheeshwar Sri Narendran introduced "Urjjameva Brahma" (ऊर्जैव ब्रह्म) as the fifth contemporary Mahavakya.'
              )}
            </p>

            {/* 1. Etymology */}
            <div className="mb-10">
              <h3 className="text-lg md:text-xl font-heading text-saffron-700 mb-4">
                1. {t('fifth_etym_heading', 'Etymological Breakdown')}
              </h3>
              <ul className="space-y-3 text-charcoal-700">
                <li className="flex gap-3">
                  <span className="text-saffron-600 font-bold mt-1">•</span>
                  <span className="leading-relaxed">
                    <span className="font-heading text-charcoal-800">Urjja (ऊर्जा): </span>
                    {t('fifth_etym_urjja', 'Dynamic Cosmic Energy, Vital Force, Shakti, or Prana.')}
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="text-saffron-600 font-bold mt-1">•</span>
                  <span className="leading-relaxed">
                    <span className="font-heading text-charcoal-800">Eva (एव): </span>
                    {t('fifth_etym_eva', 'Verily / Indeed, alone.')}
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="text-saffron-600 font-bold mt-1">•</span>
                  <span className="leading-relaxed">
                    <span className="font-heading text-charcoal-800">Brahman (ब्रह्म): </span>
                    {t('fifth_etym_brahman', 'The Absolute Supreme Consciousness.')}
                  </span>
                </li>
              </ul>
              <div className="mt-5 bg-white rounded-lg border-l-4 border-saffron-500 px-5 py-3">
                <span className="font-heading text-charcoal-800">Core Declaration: </span>
                <span className="italic text-charcoal-700">
                  &ldquo;{t('fifth_core_declaration', 'Verily, Cosmic Energy is Brahman.')}&rdquo;
                </span>
              </div>
            </div>

            {/* 2. Two-Fold Cosmic Law */}
            <div className="mb-10">
              <h3 className="text-lg md:text-xl font-heading text-saffron-700 mb-4">
                2. {t('fifth_law_heading', 'The Two-Fold Cosmic Law')}
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-white rounded-xl border border-charcoal-100 p-5">
                  <h4 className="font-heading text-charcoal-800 mb-2">
                    {t('fifth_law_1_title', 'Consciousness Governs Energy')}
                  </h4>
                  <p className="text-sm md:text-base text-charcoal-600 leading-relaxed">
                    {t(
                      'fifth_law_1_desc',
                      'Pure Unmanifest Consciousness (Chaitanya/Shiva) is the architect that enlivens, directs, and structures all energetic forces.'
                    )}
                  </p>
                </div>
                <div className="bg-white rounded-xl border border-charcoal-100 p-5">
                  <h4 className="font-heading text-charcoal-800 mb-2">
                    {t('fifth_law_2_title', 'Energy Rules the World')}
                  </h4>
                  <p className="text-sm md:text-base text-charcoal-600 leading-relaxed">
                    {t(
                      'fifth_law_2_desc',
                      'In the material universe (Prakriti), every subatomic particle, biological cell, and mental thought is driven by dynamic energy.'
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* 3. Significance */}
            <div>
              <h3 className="text-lg md:text-xl font-heading text-saffron-700 mb-4">
                3. {t('fifth_sig_heading', 'Significance for Modern Seekers')}
              </h3>
              <div className="space-y-4">
                <div className="bg-white rounded-xl border border-charcoal-100 p-5">
                  <h4 className="font-heading text-charcoal-800 mb-2">
                    {t('fifth_sig_1_title', 'Scientific Synthesis')}
                  </h4>
                  <p className="text-sm md:text-base text-charcoal-600 leading-relaxed">
                    {t(
                      'fifth_sig_1_desc',
                      "Connects modern science's discovery that matter is energy with the Vedic realization that Energy is non-different from Absolute Consciousness."
                    )}
                  </p>
                </div>
                <div className="bg-white rounded-xl border border-charcoal-100 p-5">
                  <h4 className="font-heading text-charcoal-800 mb-2">
                    {t('fifth_sig_2_title', 'Empirical Sadhana')}
                  </h4>
                  <p className="text-sm md:text-base text-charcoal-600 leading-relaxed">
                    {t(
                      'fifth_sig_2_desc',
                      'Transforms abstract philosophy into actionable discipline — mastering human energy through Pranayama, Mantra, and Agni Kriyas provides direct communion with the Divine.'
                    )}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ---------- Section 7: Unbroken Lineage & Divine Deeksha ---------- */}
      <section className="py-20 md:py-28 bg-cream" data-testid="mentor-lineage-section">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <div className="text-saffron-600 uppercase text-xs tracking-[0.2em] font-body font-semibold mb-3">
              {t('lineage_overline', 'Sacred Tradition')}
            </div>
            <h2 className="text-3xl md:text-5xl font-heading tracking-tight text-charcoal-800">
              {t('lineage_heading', 'Unbroken Lineage & Divine Deeksha')}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6 md:gap-8">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl border border-saffron-100 shadow-[0_8px_30px_rgba(44,30,22,0.06)] p-8 md:p-10"
              data-testid="lineage-card-1"
            >
              <div className="w-14 h-1 bg-saffron-500 mb-6 rounded-full" />
              <h3 className="text-xl md:text-2xl font-heading text-charcoal-800 mb-4 leading-snug">
                {t('lineage_card_1_title', 'The Unbroken Rishi Lineage')}
              </h3>
              <p className="text-base text-charcoal-600 leading-relaxed">
                {t(
                  'lineage_card_1_text',
                  'Risheeshwar Narendranji stands in an unbroken succession of spiritual masters extending back to the ancient Rishis who first received the Vedic revelations, ensuring the authentic purity of the wisdom passed from Master to disciple.'
                )}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl border border-saffron-100 shadow-[0_8px_30px_rgba(44,30,22,0.06)] p-8 md:p-10"
              data-testid="lineage-card-2"
            >
              <div className="w-14 h-1 bg-saffron-500 mb-6 rounded-full" />
              <h3 className="text-xl md:text-2xl font-heading text-charcoal-800 mb-4 leading-snug">
                {t('lineage_card_2_title', 'The Grace of Maharishi Agastya')}
              </h3>
              <p className="text-base text-charcoal-600 leading-relaxed">
                {t(
                  'lineage_card_2_text',
                  'His spiritual journey reached supreme realization through direct initiation (Deeksha) bestowed by Maharishi Agastya — the legendary Vedic Rishi, master of Siddha science, and eternal guardian of cosmic wisdom.'
                )}
              </p>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};
